import React, { useMemo, useState } from "react";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Button,
  TextField,
  Alert,
  InputAdornment,
} from "@mui/material";

import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { createCommonStyles } from "../theme/styles";
import authService from "../services/authService";
import { loginUser } from "../store/slices/authSlice";
import { getTenantSubdomain } from "../utils/subdomain";

const ForgotPassword = () => {
  const { isDarkMode } = useAppTheme();
  const commonStyles = createCommonStyles(isDarkMode);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [email, setEmail] = useState("");
  const [result, setResult] = useState({});
  const [error, setError] = useState(null);
  const [code, setCode] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tenantSubdomain = useMemo(() => getTenantSubdomain(), []);

  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const sendForgotPassword = async (e) => {
    try {
      setResult({});
      setError(null);
      const response = await authService.sendForgotPassword(email);
      setResult({ ...response });
    } catch (err) {
      setError(
        "Nazalost nismo pronasli nijedan nalog. Molim Vas probajte sa drugim e-mailom."
      );
    }
  };

  const confirmCode = async () => {
    try {
      const data = {
        code: code,
        email: email,
      };
      const response = await authService.confirmCode(data);
      navigate("/resetpassword", { state: { userId: response._id } });
    } catch (error) {
      setError("Pogresan kod.");
    }
  };

  const login = async () => {
    const result = await dispatch(loginUser({ ...formData, subdomain: tenantSubdomain }));
    if (loginUser.fulfilled.match(result)) navigate("/dashboard");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          ml: { sm: `${72}px` },
          background: isDarkMode 
            ? "linear-gradient(180deg, #0F1431 0%, #171B46 100%)"
            : "#ffffff",
          boxShadow: isDarkMode 
            ? "0 4px 20px rgba(0,0,0,0.3)" 
            : "0 4px 20px rgba(0,0,0,0.1)",
          transition: "width 0.3s, margin 0.3s",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Logo levo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={require("../assets/ZakazujMeLogo4.png")}
              alt="Logo"
              style={{ height: 80, objectFit: "contain" }} // možeš menjati visinu po potrebi
            />
          </Box>

          {/* Desna strana - Email + Lozinka + Dugme */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              id="email"
              label="E-mail Adresa"
              name="email"
              value={formData.email}
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "common.white",
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.06)",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
                },
                "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                width: 300,
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              id="password"
              label="Lozinka"
              name="password"
              type={showPwd ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "common.white",
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.06)",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
                },
                "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                width: 300,
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPwd((s) => !s)}
                      edge="end"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              onClick={login}
              type="submit"
              variant="contained"
              sx={{
                py: 1,
                px: 3,
                borderRadius: 999,
                fontWeight: 700,
                textTransform: "none",
                background:
                  "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)",
                boxShadow: "0 12px 28px rgba(124, 58, 237, 0.35)",
                "&:hover": {
                  boxShadow: "0 16px 36px rgba(124, 58, 237, 0.55)",
                },
              }}
            >
              Log In
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 10,
          transition: "margin 0.3s, width 0.3s",
          background: isDarkMode 
            ? "radial-gradient(1200px 800px at 20% 30%, #1e2a78 0%, #0b0f2b 50%, #0a0d20 100%)"
            : "#ffffff",
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start", // box ide ka vrhu
            minHeight: "100vh",
            pt: 12, // padding-top (razmak od AppBar-a)
            background: isDarkMode 
              ? "radial-gradient(1200px 800px at 20% 30%, #1e2a78 0%, #0b0f2b 50%, #0a0d20 100%)"
              : "#ffffff",
          }}
        >
          <Box
            sx={{
              ...commonStyles.glassContainer,
              p: 4,
              width: "100%",
              maxWidth: 420,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Naslov */}
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "white", mb: 1 }}
            >
              Nađite svoj nalog
            </Typography>

            {/* Podnaslov */}
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}
            >
              {result.success ? "Ukucajte kod" : "Ukucajte vaš email"}
            </Typography>
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2, ...commonStyles.alert("error") }}
              >
                {error}
              </Alert>
            )}
            {result && result.success === true && (
              <Alert
                severity="success"
                sx={{ mb: 2, ...commonStyles.alert("success") }}
              >
                {result.message}
              </Alert>
            )}

            {/* Input polje */}
            {result.success ? (
              <TextField
                fullWidth
                id="searchCode"
                label=""
                variant="outlined"
                value={code}
                placeholder="Ovde unesite kod"
                onChange={(e) => setCode(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "common.white",
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.06)",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.35)",
                    },
                  },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                  mb: 3,
                }}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <TextField
                fullWidth
                value={email}
                id="searchEmail"
                label={"E-mail"}
                variant="outlined"
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "common.white",
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.06)",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.35)",
                    },
                  },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                  mb: 3,
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}

            {/* Dugmad desno */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: 999,
                  px: 3,
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "white",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.6)",
                    backgroundColor: "rgba(255,255,255,0.05)",
                  },
                }}
                onClick={() => navigate("/login")}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={result.success ? confirmCode : sendForgotPassword}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 700,
                  textTransform: "none",
                  background:
                    "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)",
                  boxShadow: "0 12px 28px rgba(124, 58, 237, 0.35)",
                  "&:hover": {
                    boxShadow: "0 16px 36px rgba(124, 58, 237, 0.55)",
                  },
                }}
              >
                {result.success ? "Potvrdi" : "Potrazi"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
